import { useState, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import { TempLogo } from "@/assets/temp-logo";
import Footer from "@/components/Common/Footer";
import { useAuth } from "../hooks/useAuth";
import { useRegister } from "../api/auth.queries";
import { extractErrorMessage } from "@/lib/errors";
import VerifyEmail from "./VerifyEmailContainer";
import { registerSchema, type RegisterSchema } from "@/schemas/auth.schemas";
import { Spinner } from "@/components/ui/spinner";

export default function Register() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showVerifyEmail, setShowVerifyEmail] = useState(false);

  const form = useForm<RegisterSchema>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const { mutate: register, isPending } = useRegister();
  const { setError } = form;

  // Redirect if logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate({ to: "/", search: { section: undefined } });
    }
  }, [isAuthenticated, navigate]);

  function onSubmit(values: RegisterSchema) {
    register(
      {
        email: values.email,
        password: values.password,
        username: values.username,
        first_name: values.first_name,
        last_name: values.last_name,
        provider: "email",
      },
      {
        onSuccess: () => {
          setShowVerifyEmail(true);
        },
        onError: (err) => {
          const message = extractErrorMessage(err);

          if (message.includes("email")) {
            setError("email", { type: "server", message });
          } else if (message.includes("username")) {
            setError("username", { type: "server", message });
          } else {
            setError("root", { type: "server", message });
          }
        },
      },
    );
  }

  if (showVerifyEmail) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-4 pb-8">
        <VerifyEmail forgotPassword />
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-between px-4 pb-8">
      <div className="flex w-full max-w-[420px] flex-grow flex-col justify-center gap-8">
        {/* Logo */}
        <div className="text-center">
          <div className="flex justify-center">
            <TempLogo width={68} height={44} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">metawhale</h1>
        </div>

        {/* Back */}
        <p
          className="text-muted mb-8 flex cursor-pointer items-center gap-1 text-sm font-bold"
          onClick={() => navigate({ to: "/login" })}
        >
          <i className="ri-arrow-left-long-line"></i>
          Back
        </p>

        {/* Form */}
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col space-y-3"
          >
            {/* Names */}
            <div className="grid grid-cols-2 items-start gap-3">
              <FormField
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className="text-sm font-bold text-neutral-800">
                      First Name <span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="text"
                        placeholder="John"
                        variant="filled"
                        className="h-[45px]"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className="text-sm font-bold text-neutral-800">
                      Last Name <span className="text-red-600">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="text"
                        placeholder="Appleseed"
                        variant="filled"
                        className="h-[45px]"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            {/* Username */}
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel className="text-sm font-bold text-neutral-800">
                    Username <span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="text"
                      placeholder="yourusername"
                      variant="filled"
                      className="h-[45px]"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Email */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel className="text-sm font-bold text-neutral-800">
                    Email Address <span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      placeholder="email@example.com"
                      variant="filled"
                      className="h-[45px]"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Password */}
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel className="text-sm font-bold text-neutral-800">
                    Password <span className="text-red-600">*</span>
                  </FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input
                        {...field}
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••••••"
                        variant="filled"
                        className="h-[45px]"
                      />
                    </FormControl>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute top-1/2 right-3 h-6 w-6 -translate-y-1/2 text-neutral-800"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <i className="ri-eye-off-line" />
                      ) : (
                        <i className="ri-eye-line" />
                      )}
                    </Button>
                  </div>
                  <FormMessage className="min-h-[16px] text-xs text-red-600" />
                </FormItem>
              )}
            />

            {/* Confirm Password */}
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel className="text-sm font-bold text-neutral-800">
                    Confirm Password <span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••••••"
                      variant="filled"
                      className="h-[45px]"
                    />
                  </FormControl>
                  <FormMessage className="min-h-[16px] text-xs text-red-600" />
                </FormItem>
              )}
            />

            {/* Submit */}
            <Button
              type="submit"
              className="h-[48px] w-full"
              disabled={isPending}
            >
              {isPending ? (
                <div className="flex h-full items-center justify-center text-white">
                  <Spinner size="sm" className="stroke-white!" />
                </div>
              ) : (
                "Register"
              )}
            </Button>
          </form>
        </Form>
      </div>
      <Footer />
    </div>
  );
}
